import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useAuth } from '@modules/auth/hooks';
import { findByPatientId } from '@modules/genetic-counseling/services';

import { FIND_PARENT_ID_KEY } from '../constants';
import { FindPatientIdError, FindPatientIdParams, PatientProfileResponse } from '../types';

const useQueryFindPatientId = (
  { onError, onSettled, onSuccess, patientId }: FindPatientIdParams,
  options?: Omit<
    UseQueryOptions<
      PatientProfileResponse | undefined,
      AxiosError,
      PatientProfileResponse | undefined,
      Array<string | undefined>
    >,
    'initialData' | 'queryFn' | 'queryKey'
  >,
): UseQueryResult<PatientProfileResponse | undefined> => {
  const { isAuthenticated } = useAuth();

  const enabledOption = options?.enabled !== undefined ? !!options?.enabled : true;

  const onSuccessFn = (data: PatientProfileResponse | undefined): void => {
    onSuccess?.(data);
  };

  const onErrorFn = (err: AxiosError): void => {
    let errorMsg = err.message;
    // handle 404 when patient ID is NOT found.
    if (err.response?.status === 404) {
      errorMsg = FindPatientIdError.NO_MATCHING_ID;
    }

    onError?.({
      ...err,
      message: errorMsg,
    });
  };

  const onSettledFn = (_data: PatientProfileResponse | undefined, _error: unknown): void => {
    onSettled?.();
  };

  return useQuery([FIND_PARENT_ID_KEY, patientId], async () => await findByPatientId({ patientId }), {
    ...options,
    onSuccess: onSuccessFn,
    onError: onErrorFn,
    onSettled: onSettledFn,
    enabled: isAuthenticated === true && enabledOption,
  });
};

export default useQueryFindPatientId;
